/**
 * Auto Stacking API Service
 * Handles auto stacking settings endpoints
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    AutoStackingSettings,
    AutoStackingSettingsResponse,
    UpdateAutoStackingSettingsRequest,
    AutoStackingTransactionFee,
    CreateAutoStackingTransactionFeeRequest,
    UpdateAutoStackingTransactionFeeRequest,
    BulkAutoStackingTransactionFeesRequest,
} from '@/types/auto-stacking'

/**
 * Get auto stacking settings
 * GET /admin/auto-stacking
 */
export const getAutoStackingSettings =
    async (): Promise<AutoStackingSettings> => {
        const response = await axiosInstance.get<AutoStackingSettingsResponse>(
            API_ROUTES.AUTO_STACKING.SETTINGS
        )
        return response.data.data
    }

/**
 * Update auto stacking settings
 * PATCH /admin/auto-stacking
 */
export const updateAutoStackingSettings = async (
    data: UpdateAutoStackingSettingsRequest
): Promise<AutoStackingSettings> => {
    const response = await axiosInstance.patch<AutoStackingSettingsResponse>(
        API_ROUTES.AUTO_STACKING.SETTINGS,
        data
    )
    return response.data.data
}

/**
 * Create a transaction fee range
 * POST /admin/auto-stacking/transaction-fees
 */
export const createAutoStackingTransactionFee = async (
    data: CreateAutoStackingTransactionFeeRequest
): Promise<AutoStackingTransactionFee> => {
    const response = await axiosInstance.post<{
        status: string
        statusCode: number
        message: string
        data: AutoStackingTransactionFee
    }>(API_ROUTES.AUTO_STACKING.TRANSACTION_FEES, data)
    return response.data.data
}

/**
 * Update a transaction fee range
 * PATCH /admin/auto-stacking/transaction-fees/{feeId}
 */
export const updateAutoStackingTransactionFee = async (
    feeId: string,
    data: UpdateAutoStackingTransactionFeeRequest
): Promise<AutoStackingTransactionFee> => {
    const response = await axiosInstance.patch<{
        status: string
        statusCode: number
        message: string
        data: AutoStackingTransactionFee
    }>(API_ROUTES.AUTO_STACKING.UPDATE_TRANSACTION_FEE(feeId), data)
    return response.data.data
}

/**
 * Delete a transaction fee range
 * DELETE /admin/auto-stacking/transaction-fees/{feeId}
 */
export const deleteAutoStackingTransactionFee = async (
    feeId: string
): Promise<void> => {
    await axiosInstance.delete(
        API_ROUTES.AUTO_STACKING.DELETE_TRANSACTION_FEE(feeId)
    )
}

/**
 * Bulk create transaction fee ranges
 * POST /admin/auto-stacking/transaction-fees/bulk
 */
export const bulkCreateAutoStackingTransactionFees = async (
    data: BulkAutoStackingTransactionFeesRequest
): Promise<AutoStackingTransactionFee[]> => {
    const response = await axiosInstance.post<{
        status: string
        statusCode: number
        message: string
        data: AutoStackingTransactionFee[]
    }>(API_ROUTES.AUTO_STACKING.TRANSACTION_FEES_BULK, data)
    return response.data.data
}
